import React, { createContext, ReactNode, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { getLanguageFromPreferenceName } from '../i18n/languages';

export type KundliChartStyle = 'north' | 'south' | 'east';
export type AppMode = 'light' | 'dark' | 'night';
export type NodeMode = 'true' | 'mean';
export type AyanamshaMode = 'LAHIRI' | 'KP' | 'RAMAN' | 'YUKTESHWAR' | 'TRUE_CITRA' | 'TRUE_REVATI' | 'SURYASIDDHANTA' | 'FAGAN_BRADLEY';
export type ThemePaletteName = 'green' | 'blue' | 'yellow' | 'pink' | 'orange' | 'purple';
export type HouseSystemMode = 'KP_PLACIDUS' | 'PLACIDUS' | 'EQUAL';
export type DefaultCity = {
  id: string;
  name: string;
  displayName: string;
  coordinates: {
    latitude: number;
    longitude: number;
  };
  timezone: {
    name: string;
    offset: number;
  };
  population?: number;
  admin1?: {
    name: string;
    code?: string;
  };
  country?: {
    name: string;
    code: string;
  };
};

const CHART_STYLE_STORAGE_KEY = 'kundlistarChartStyle';
const APP_MODE_STORAGE_KEY = 'kundlistarMode';
const DEFAULT_CITY_STORAGE_KEY = 'kundlistarDefaultCity';
const NODE_MODE_STORAGE_KEY = 'kundlistarNodeMode';
const HOUSE_SYSTEM_STORAGE_KEY = 'kundlistarHouseSystem';
const AYANAMSHA_MODE_STORAGE_KEY = 'kundlistarAyanamshaMode';
const USE_KP_FOR_NADI_KP_STORAGE_KEY = 'kundlistarUseKpForNadiKp';
const SHOW_OUTER_PLANETS_STORAGE_KEY = 'kundlistarShowOuterPlanets';
const THEME_PALETTE_STORAGE_KEY = 'kundlistarThemePalette';
const AUTH_TOKEN_KEY = 'nadiAstroToken';

const OUTER_PLANET_KEYS = new Set(['URANUS', 'NEPTUNE', 'PLUTO']);
export const AYANAMSHA_OPTIONS: { label: string; hindiLabel: string; value: AyanamshaMode }[] = [
  { label: 'Lahiri / Chitrapaksha', hindiLabel: 'लाहिड़ी / चित्रपक्ष', value: 'LAHIRI' },
  { label: 'KP / Krishnamurti', hindiLabel: 'KP / कृष्णमूर्ति', value: 'KP' },
  { label: 'Raman', hindiLabel: 'रमन', value: 'RAMAN' },
  { label: 'Yukteshwar', hindiLabel: 'युक्तेश्वर', value: 'YUKTESHWAR' },
  { label: 'True Chitra', hindiLabel: 'ट्रू चित्रा', value: 'TRUE_CITRA' },
  { label: 'True Revati', hindiLabel: 'ट्रू रेवती', value: 'TRUE_REVATI' },
  { label: 'Suryasiddhanta', hindiLabel: 'सूर्य सिद्धांत', value: 'SURYASIDDHANTA' },
  { label: 'Fagan / Bradley', hindiLabel: 'फेगन / ब्रैडली', value: 'FAGAN_BRADLEY' },
];

export const HOUSE_SYSTEM_OPTIONS: { label: string; hindiLabel: string; value: HouseSystemMode }[] = [
  { label: 'KP Bhava Chalit / Placidus', hindiLabel: 'KP भाव चलित / प्लेसिडस', value: 'KP_PLACIDUS' },
  { label: 'Placidus cusps', hindiLabel: 'प्लेसिडस कस्प', value: 'PLACIDUS' },
  { label: 'Equal house', hindiLabel: 'इक्वल हाउस', value: 'EQUAL' },
];

export const isOuterPlanetKey = (keyOrName: unknown) => (
  OUTER_PLANET_KEYS.has(String(keyOrName || '').trim().toUpperCase())
);

export const THEME_PALETTE_OPTIONS: { label: string; hindiLabel: string; value: ThemePaletteName; swatch: string }[] = [
  { label: 'Default', hindiLabel: 'डिफॉल्ट', value: 'green', swatch: '#0F6E56' },
  { label: 'Blue', hindiLabel: 'नीला', value: 'blue', swatch: '#0b84ff' },
  { label: 'Yellow', hindiLabel: 'पीला', value: 'yellow', swatch: '#f2b900' },
  { label: 'Pink', hindiLabel: 'गुलाबी', value: 'pink', swatch: '#e84d9a' },
  { label: 'Orange', hindiLabel: 'नारंगी', value: 'orange', swatch: '#ff6a00' },
  { label: 'Purple', hindiLabel: 'बैंगनी', value: 'purple', swatch: '#8e4de8' },
];

export const THEME_PALETTE_COLORS: Record<ThemePaletteName, { main: string; light: string; dark: string; secondary: string }> = {
  green: { main: '#0F6E56', light: '#E1F5EE', dark: '#0A5240', secondary: '#085041' },
  blue: { main: '#0b84ff', light: '#64b5ff', dark: '#0758b8', secondary: '#38bdf8' },
  yellow: { main: '#f2b900', light: '#ffd54f', dark: '#8a6500', secondary: '#f59e0b' },
  pink: { main: '#e84d9a', light: '#f48fbd', dark: '#a51661', secondary: '#ec4899' },
  orange: { main: '#ff6a00', light: '#fb923c', dark: '#b45309', secondary: '#f97316' },
  purple: { main: '#8e4de8', light: '#b674ff', dark: '#5f21b5', secondary: '#a855f7' },
};

interface UserPreferencesContextValue {
  chartStyle: KundliChartStyle;
  setChartStyle: (style: KundliChartStyle) => void;
  mode: AppMode;
  setMode: (mode: AppMode) => void;
  themePalette: ThemePaletteName;
  setThemePalette: (palette: ThemePaletteName) => void;
  defaultCity: DefaultCity | null;
  setDefaultCity: (city: DefaultCity | null) => void;
  nodeMode: NodeMode;
  setNodeMode: (mode: NodeMode) => void;
  houseSystem: HouseSystemMode;
  setHouseSystem: (system: HouseSystemMode) => void;
  ayanamshaMode: AyanamshaMode;
  setAyanamshaMode: (mode: AyanamshaMode) => void;
  useKpForNadiKp: boolean;
  setUseKpForNadiKp: (useKp: boolean) => void;
  showOuterPlanets: boolean;
  setShowOuterPlanets: (show: boolean) => void;
}

const UserPreferencesContext = createContext<UserPreferencesContextValue>({
  chartStyle: 'north',
  setChartStyle: () => {},
  mode: 'light',
  setMode: () => {},
  themePalette: 'green',
  setThemePalette: () => {},
  defaultCity: null,
  setDefaultCity: () => {},
  nodeMode: 'true',
  setNodeMode: () => {},
  houseSystem: 'KP_PLACIDUS',
  setHouseSystem: () => {},
  ayanamshaMode: 'LAHIRI',
  setAyanamshaMode: () => {},
  useKpForNadiKp: true,
  setUseKpForNadiKp: () => {},
  showOuterPlanets: false,
  setShowOuterPlanets: () => {},
});

const isChartStyle = (value: string | null): value is KundliChartStyle => (
  value === 'north' || value === 'south' || value === 'east'
);

const isAppMode = (value: string | null): value is AppMode => value === 'light' || value === 'dark' || value === 'night';
const isNodeMode = (value: string | null): value is NodeMode => value === 'true' || value === 'mean';
const isHouseSystemMode = (value: string | null): value is HouseSystemMode => (
  value === 'KP_PLACIDUS' || value === 'PLACIDUS' || value === 'EQUAL'
);
const isAyanamshaMode = (value: string | null): value is AyanamshaMode => (
  AYANAMSHA_OPTIONS.some((option) => option.value === value)
);
const isThemePalette = (value: string | null): value is ThemePaletteName => (
  THEME_PALETTE_OPTIONS.some((option) => option.value === value)
);
const toThemePaletteFromHex = (value: unknown): ThemePaletteName | null => {
  const normalized = String(value || '').trim().toLowerCase();
  if (!/^#[0-9a-f]{6}$/.test(normalized)) return null;
  const match = THEME_PALETTE_OPTIONS.find((option) => option.swatch.toLowerCase() === normalized);
  return match?.value || null;
};
const toServerChartStyle = (style: KundliChartStyle) => style.charAt(0).toUpperCase() + style.slice(1);

const toLocalChartStyle = (style: unknown): KundliChartStyle | null => {
  const normalized = String(style || '').toLowerCase();
  return isChartStyle(normalized) ? normalized : null;
};

const syncPreferencesToServer = (preferences: Record<string, unknown>) => {
  if (localStorage.getItem(AUTH_TOKEN_KEY)) {
    axios.put('/users/profile', { preferences }).catch(() => {});
  }
};

export const UserPreferencesProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const { i18n } = useTranslation();
  const [chartStyle, setChartStyleState] = useState<KundliChartStyle>(() => {
    const storedChartStyle = localStorage.getItem(CHART_STYLE_STORAGE_KEY);
    return isChartStyle(storedChartStyle) ? storedChartStyle : 'north';
  });
  const [mode, setModeState] = useState<AppMode>(() => {
    const storedMode = localStorage.getItem(APP_MODE_STORAGE_KEY);
    return isAppMode(storedMode) ? storedMode : 'light';
  });
  const [themePalette, setThemePaletteState] = useState<ThemePaletteName>(() => {
    const storedPalette = localStorage.getItem(THEME_PALETTE_STORAGE_KEY);
    return isThemePalette(storedPalette) ? storedPalette : 'green';
  });
  const [defaultCity, setDefaultCityState] = useState<DefaultCity | null>(() => {
    const storedDefaultCity = localStorage.getItem(DEFAULT_CITY_STORAGE_KEY);
    if (!storedDefaultCity) return null;
    try {
      return JSON.parse(storedDefaultCity);
    } catch {
      localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
      return null;
    }
  });
  const [nodeMode, setNodeModeState] = useState<NodeMode>(() => {
    const storedNodeMode = localStorage.getItem(NODE_MODE_STORAGE_KEY);
    return isNodeMode(storedNodeMode) ? storedNodeMode : 'true';
  });
  const [houseSystem, setHouseSystemState] = useState<HouseSystemMode>(() => {
    const storedHouseSystem = localStorage.getItem(HOUSE_SYSTEM_STORAGE_KEY);
    return isHouseSystemMode(storedHouseSystem) ? storedHouseSystem : 'KP_PLACIDUS';
  });
  const [ayanamshaMode, setAyanamshaModeState] = useState<AyanamshaMode>(() => {
    const storedAyanamshaMode = localStorage.getItem(AYANAMSHA_MODE_STORAGE_KEY);
    return isAyanamshaMode(storedAyanamshaMode) ? storedAyanamshaMode : 'LAHIRI';
  });
  const [useKpForNadiKp, setUseKpForNadiKpState] = useState(() => (
    localStorage.getItem(USE_KP_FOR_NADI_KP_STORAGE_KEY) !== 'false'
  ));
  const [showOuterPlanets, setShowOuterPlanetsState] = useState(() => (
    localStorage.getItem(SHOW_OUTER_PLANETS_STORAGE_KEY) === 'true'
  ));

  useEffect(() => {
    document.documentElement.dataset.theme = mode;
    const pageBackground = mode === 'night' ? '#050805' : mode === 'dark' ? '#07130D' : '#F6F7F6';
    document.documentElement.style.backgroundColor = pageBackground;
    document.body.style.backgroundColor = pageBackground;
  }, [mode]);

  useEffect(() => {
    const syncServerDefaultCity = async () => {
      if (!localStorage.getItem(AUTH_TOKEN_KEY)) return;
      try {
        const response = await axios.get('/users/profile');
        const serverUser = response.data?.data?.user || {};
        const serverPreferences = serverUser.preferences || {};
        const serverChartStyle = toLocalChartStyle(serverPreferences.chartStyle);
        const serverMode = isAppMode(serverPreferences.themeMode) ? serverPreferences.themeMode : null;
        const serverThemePalette = isThemePalette(serverPreferences.themePalette)
          ? serverPreferences.themePalette
          : toThemePaletteFromHex(serverPreferences.themePalette)
            || toThemePaletteFromHex(serverPreferences.themeColor)
            || toThemePaletteFromHex(serverUser.reportBranding?.themeColor);
        const serverNodeMode = isNodeMode(serverPreferences.nodeMode) ? serverPreferences.nodeMode : null;
        const serverHouseSystem = isHouseSystemMode(serverPreferences.houseSystem) ? serverPreferences.houseSystem : null;
        const serverAyanamshaMode = isAyanamshaMode(String(serverPreferences.ayanamshaMode || '').toUpperCase())
          ? String(serverPreferences.ayanamshaMode).toUpperCase() as AyanamshaMode
          : null;
        const serverUseKpForNadiKp = serverPreferences.useKpForNadiKp !== false;
        const serverShowOuterPlanets = serverPreferences.showOuterPlanets === true;
        const serverDefaultCity = serverPreferences.defaultCity || null;

        if (serverChartStyle) {
          setChartStyleState(serverChartStyle);
          localStorage.setItem(CHART_STYLE_STORAGE_KEY, serverChartStyle);
        }
        if (serverMode) {
          setModeState(serverMode);
          localStorage.setItem(APP_MODE_STORAGE_KEY, serverMode);
        }
        if (serverThemePalette) {
          setThemePaletteState(serverThemePalette);
          localStorage.setItem(THEME_PALETTE_STORAGE_KEY, serverThemePalette);
        }
        if (serverNodeMode) {
          setNodeModeState(serverNodeMode);
          localStorage.setItem(NODE_MODE_STORAGE_KEY, serverNodeMode);
        }
        if (serverHouseSystem) {
          setHouseSystemState(serverHouseSystem);
          localStorage.setItem(HOUSE_SYSTEM_STORAGE_KEY, serverHouseSystem);
        }
        if (serverAyanamshaMode) {
          setAyanamshaModeState(serverAyanamshaMode);
          localStorage.setItem(AYANAMSHA_MODE_STORAGE_KEY, serverAyanamshaMode);
        }
        setUseKpForNadiKpState(serverUseKpForNadiKp);
        localStorage.setItem(USE_KP_FOR_NADI_KP_STORAGE_KEY, String(serverUseKpForNadiKp));
        setShowOuterPlanetsState(serverShowOuterPlanets);
        localStorage.setItem(SHOW_OUTER_PLANETS_STORAGE_KEY, String(serverShowOuterPlanets));
        const localLanguage = localStorage.getItem('kundlistarLanguage');
        if (localLanguage) {
          i18n.changeLanguage(getLanguageFromPreferenceName(localLanguage));
        } else if (serverPreferences.language) {
          i18n.changeLanguage(getLanguageFromPreferenceName(serverPreferences.language));
        }
        if (serverDefaultCity?.id) {
          setDefaultCityState(serverDefaultCity);
          localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(serverDefaultCity));
        } else if (serverDefaultCity === null) {
          setDefaultCityState(null);
          localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
        }
      } catch {
        // Keep the local preference if profile sync is temporarily unavailable.
      }
    };

    syncServerDefaultCity();
  }, [i18n]);

  const setChartStyle = useCallback((style: KundliChartStyle) => {
    setChartStyleState(style);
    localStorage.setItem(CHART_STYLE_STORAGE_KEY, style);
    syncPreferencesToServer({ chartStyle: toServerChartStyle(style) });
  }, []);

  const setMode = useCallback((nextMode: AppMode) => {
    setModeState(nextMode);
    localStorage.setItem(APP_MODE_STORAGE_KEY, nextMode);
    syncPreferencesToServer({ themeMode: nextMode });
  }, []);

  const setThemePalette = useCallback((nextPalette: ThemePaletteName) => {
    setThemePaletteState(nextPalette);
    localStorage.setItem(THEME_PALETTE_STORAGE_KEY, nextPalette);
    syncPreferencesToServer({ themePalette: nextPalette });
  }, []);

  const setDefaultCity = useCallback((city: DefaultCity | null) => {
    setDefaultCityState(city);
    if (city) {
      localStorage.setItem(DEFAULT_CITY_STORAGE_KEY, JSON.stringify(city));
    } else {
      localStorage.removeItem(DEFAULT_CITY_STORAGE_KEY);
    }
    syncPreferencesToServer({ defaultCity: city });
  }, []);

  const setNodeMode = useCallback((nextNodeMode: NodeMode) => {
    setNodeModeState(nextNodeMode);
    localStorage.setItem(NODE_MODE_STORAGE_KEY, nextNodeMode);
    syncPreferencesToServer({ nodeMode: nextNodeMode });
  }, []);

  const setHouseSystem = useCallback((nextHouseSystem: HouseSystemMode) => {
    setHouseSystemState(nextHouseSystem);
    localStorage.setItem(HOUSE_SYSTEM_STORAGE_KEY, nextHouseSystem);
    syncPreferencesToServer({ houseSystem: nextHouseSystem });
  }, []);

  const setAyanamshaMode = useCallback((nextAyanamshaMode: AyanamshaMode) => {
    setAyanamshaModeState(nextAyanamshaMode);
    localStorage.setItem(AYANAMSHA_MODE_STORAGE_KEY, nextAyanamshaMode);
    syncPreferencesToServer({ ayanamshaMode: nextAyanamshaMode });
  }, []);

  const setUseKpForNadiKp = useCallback((useKp: boolean) => {
    setUseKpForNadiKpState(useKp);
    localStorage.setItem(USE_KP_FOR_NADI_KP_STORAGE_KEY, String(useKp));
    syncPreferencesToServer({ useKpForNadiKp: useKp });
  }, []);

  const setShowOuterPlanets = useCallback((show: boolean) => {
    setShowOuterPlanetsState(show);
    localStorage.setItem(SHOW_OUTER_PLANETS_STORAGE_KEY, String(show));
    syncPreferencesToServer({ showOuterPlanets: show });
  }, []);

  const value = useMemo(
    () => ({
      chartStyle,
      setChartStyle,
      mode,
      setMode,
      themePalette,
      setThemePalette,
      defaultCity,
      setDefaultCity,
      nodeMode,
      setNodeMode,
      houseSystem,
      setHouseSystem,
      ayanamshaMode,
      setAyanamshaMode,
      useKpForNadiKp,
      setUseKpForNadiKp,
      showOuterPlanets,
      setShowOuterPlanets
    }),
    [ayanamshaMode, chartStyle, defaultCity, houseSystem, mode, nodeMode, showOuterPlanets, themePalette, useKpForNadiKp, setAyanamshaMode, setChartStyle, setDefaultCity, setHouseSystem, setMode, setNodeMode, setShowOuterPlanets, setThemePalette, setUseKpForNadiKp]
  );

  return (
    <UserPreferencesContext.Provider value={value}>
      {children}
    </UserPreferencesContext.Provider>
  );
};

export function useUserPreferences() {
  return useContext(UserPreferencesContext);
}
